import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { ISelectOption } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { DynamicMultiSelectFilters } from '../filtersTypes/dynamicFilter.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { TextFilter } from '../filtersTypes/textFilter.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { InLineControlAiService } from 'src/app/models/api/agentStep.model';
import { getAiServiceLogoPath, getAiServiceDisplayName } from 'src/app/common/aiServiceUtils';
import { checkAllGivenIdsExistInOptions, fromIntegrationToSelectOption } from './utils';
import { InLineSeveritySelectOptions } from 'src/app/models/selectOptions/inLineSeveritySelectOptions.model';
import { aiInLineControlTableColumns } from 'src/app/models/tableColumns/aiInLineControlTableColumn';
import { ITableColumn } from 'src/app/models/tableColumns';
import { IntegrationModel } from 'src/app/models/api';
import { InLineIntegrationsHttpService } from 'src/app/services/httpServices/edgeIntegrationsHttpsService/in-line-integrations-http.service';
import { InLineIntegration } from 'src/app/models/api/inLineIntegration/inLineIntegration.model';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';

export class InLineControlFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public search: TextFilter;
  public fromDate: DateFilter;
  public toDate: DateFilter;
  public severityFilters: MultiSelectFilters;
  public actorFilters: DynamicMultiSelectFilters;
  public integrationFilters: MultiSelectFilters;
  public aiServiceFilters: MultiSelectFilters;
  public environmentFilters: DynamicMultiSelectFilters;
  public threadIdFilters: DynamicMultiSelectFilters;

  public readonly defaultSortColumn: ITableColumn | undefined = aiInLineControlTableColumns.find(
    (column) => column.sortKey === 'timestamp'
  );
  public readonly sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    this.defaultSortColumn ? { column: this.defaultSortColumn, isDesc: true } : undefined
  );

  /**
   * Maps integration IDs to their corresponding inline integration IDs
   */
  private _integrationToInlineIntegrationMap: Map<string, string> = new Map();

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _inLineIntegrationsHttpService: InLineIntegrationsHttpService,
    private readonly _permissionsService: PermissionsService
  ) {
    this._initializeFilters();
    this._integrationsOnUpdate();
    this._inlineIntegrationsOnUpdate();
    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());
  }

  public getSelectedFilters(): ISelectedFilters {
    const selectedIntegrationIds = this.integrationFilters.getSelectedFiltersValues();
    const expandedIntegrationIds = this._expandIntegrationIdsWithInlineIntegrations(selectedIntegrationIds);

    return {
      search: this.search.getFilterValue(),
      fromDate: this.fromDate.getFilterValue(),
      toDate: this.toDate.getFilterValue(),
      inLineSeverity: this.severityFilters.getSelectedFiltersValues(),
      inLineActor: this.actorFilters.getSelectedFiltersValues(),
      inLineIntegration: expandedIntegrationIds,
      inLineAiService: this.aiServiceFilters.getSelectedFiltersValues(),
      inLineEnvironment: this.environmentFilters.getSelectedFiltersValues(),
      inLineThreadId: this.threadIdFilters.getSelectedFiltersValues(),
    };
  }

  /**
   * Expands integration IDs to include their matching inline integration IDs
   * @param integrationIds - Array of selected integration IDs
   * @returns Array with both original integration IDs and their matching inline integration IDs
   */
  private _expandIntegrationIdsWithInlineIntegrations(integrationIds: string[]): string[] {
    if (integrationIds.length === 0) {
      return [];
    }

    const expandedIds = new Set<string>();

    integrationIds.forEach((integrationId) => {
      // Add the original integration ID
      expandedIds.add(integrationId);

      // Add the matching inline integration ID if it exists
      const inlineIntegrationId = this._integrationToInlineIntegrationMap.get(integrationId);
      if (inlineIntegrationId) {
        expandedIds.add(inlineIntegrationId);
      }
    });

    return Array.from(expandedIds);
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selectedOptions = [
      ...this.severityFilters.getSelectedOptions(),
      ...this.actorFilters.getSelectedOptions(),
      ...this.integrationFilters.getSelectedOptions(),
      ...this.aiServiceFilters.getSelectedOptions(),
      ...this.environmentFilters.getSelectedOptions(),
      ...this.threadIdFilters.getSelectedOptions(),
    ];

    return selectedOptions;
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    let needUpdate = false;

    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.Search:
          this.search.update(value as string | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.FromDate:
          this.fromDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.ToDate:
          this.toDate.update(value as Date | undefined);
          needUpdate = true;
          break;
      }
    }

    if (value instanceof Array) {
      if (value.length == 0) {
        needUpdate = this.clearAllMultiSelectFiltersIfNeeded();
      } else {
        const optionsByCategory: {
          [key: string]: ISelectOption[];
        } = {};
        value.forEach((option) => {
          if (option.filterType) {
            if (option.filterType in optionsByCategory) {
              optionsByCategory[option.filterType].push(option);
            } else {
              optionsByCategory[option.filterType] = [option];
            }
          }
        });
        this._updateAllOptions();

        needUpdate =
          this.severityFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineSeverity] || []) || needUpdate;
        needUpdate =
          this.actorFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineActor] || []) || needUpdate;
        needUpdate =
          this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineIntegration] || []) ||
          needUpdate;
        needUpdate =
          this.aiServiceFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineAiService] || []) || needUpdate;
        needUpdate =
          this.environmentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineEnvironment] || []) ||
          needUpdate;
        needUpdate =
          this.threadIdFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.InLineThreadId] || []) || needUpdate;
      }
    }

    if (needUpdate) {
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: {
          q: filtersQuery,
        },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.InLineSeverity:
        return this.severityFilters.getOptions$();
      case FilterTypesEnum.InLineActor:
        return this.actorFilters.getOptions$();
      case FilterTypesEnum.InLineIntegration:
        return this.integrationFilters.getOptions$();
      case FilterTypesEnum.InLineAiService:
        return this.aiServiceFilters.getOptions$();
      case FilterTypesEnum.InLineEnvironment:
        return this.environmentFilters.getOptions$();
      case FilterTypesEnum.InLineThreadId:
        return this.threadIdFilters.getOptions$();
      default:
        throw new Error(`InLineControlFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};
    options.forEach(
      (option) => {
        if (option.filterType) {
          if (option.filterType in optionsIdsByCategory) {
            optionsIdsByCategory[option.filterType].add(option.id);
          } else {
            optionsIdsByCategory[option.filterType] = new Set([option.id]);
          }
        }
      },
      this.getDynamicFilters().forEach((filter) => {
        optionsIdsByCategory[filter] = new Set();
      })
    );

    if (optionsIdsByCategory[FilterTypesEnum.InLineSeverity]) {
      this.severityFilters.updateIfNeeded(
        this.severityFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineSeverity] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineSeverity].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineActor]) {
      this.actorFilters.updateIfNeeded(
        this.actorFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineActor] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineActor].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineIntegration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineIntegration] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineIntegration].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineAiService]) {
      this.aiServiceFilters.updateIfNeeded(
        this.aiServiceFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineAiService] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineAiService].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineEnvironment]) {
      this.environmentFilters.updateIfNeeded(
        this.environmentFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.InLineEnvironment].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.InLineThreadId]) {
      this.threadIdFilters.updateIfNeeded(
        this.threadIdFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.InLineThreadId] &&
              !optionsIdsByCategory[FilterTypesEnum.InLineThreadId].has(selectedOption.id)
          )
      );
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: {
        q: filtersQuery,
      },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.InLineSeverity:
        return this.severityFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineActor:
        return this.actorFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineIntegration:
        return this.integrationFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineAiService:
        return this.aiServiceFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineEnvironment:
        return this.environmentFilters.getSelectedFiltersIds();
      case FilterTypesEnum.InLineThreadId:
        return this.threadIdFilters.getSelectedFiltersIds();
      default:
        throw new Error(`InLineControlFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(
    keyValueFilters: { [key: string]: string[] | string | Date },
    updateQueryParams = false
  ): void {
    let needUpdate = false;
    let values: string[] = [];

    // Handle search filter
    if ('search' in keyValueFilters && typeof keyValueFilters['search'] === 'string') {
      this.search.update(keyValueFilters['search']);
      needUpdate = true;
    }

    // Handle date filters
    if ('fromDate' in keyValueFilters && keyValueFilters['fromDate'] instanceof Date) {
      this.fromDate.update(keyValueFilters['fromDate']);
      needUpdate = true;
    }

    if ('toDate' in keyValueFilters && keyValueFilters['toDate'] instanceof Date) {
      this.toDate.update(keyValueFilters['toDate']);
      needUpdate = true;
    }

    values = 'inLineSeverity' in keyValueFilters ? (keyValueFilters['inLineSeverity'] as string[]) : [];
    needUpdate = this.severityFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineActor' in keyValueFilters ? (keyValueFilters['inLineActor'] as string[]) : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.actorFilters.getOptions$().value, values)) {
      this.actorFilters = new DynamicMultiSelectFilters([]);
      this._actorOnUpdate(values);
    }
    needUpdate = this.actorFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineIntegration' in keyValueFilters ? (keyValueFilters['inLineIntegration'] as string[]) : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineAiService' in keyValueFilters ? (keyValueFilters['inLineAiService'] as string[]) : [];
    needUpdate = this.aiServiceFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineEnvironment' in keyValueFilters ? (keyValueFilters['inLineEnvironment'] as string[]) : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.environmentFilters.getOptions$().value, values)) {
      this.environmentFilters = new DynamicMultiSelectFilters([]);
      this._environmentOnUpdate(values);
    }
    needUpdate = this.environmentFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'inLineThreadId' in keyValueFilters ? (keyValueFilters['inLineThreadId'] as string[]) : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.threadIdFilters.getOptions$().value, values)) {
      this.threadIdFilters = new DynamicMultiSelectFilters([]);
      this._threadIdOnUpdate(values);
    }
    needUpdate = this.threadIdFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: {
            q: filtersQuery,
          },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public clearAllMultiSelectFiltersIfNeeded(): boolean {
    return (
      this.severityFilters.updateIfNeeded([]) ||
      this.actorFilters.updateIfNeeded([]) ||
      this.integrationFilters.updateIfNeeded([]) ||
      this.aiServiceFilters.updateIfNeeded([]) ||
      this.environmentFilters.updateIfNeeded([]) ||
      this.threadIdFilters.updateIfNeeded([])
    );
  }

  public getDynamicFilters(): string[] {
    return [FilterTypesEnum.InLineActor, FilterTypesEnum.InLineEnvironment, FilterTypesEnum.InLineThreadId];
  }

  private _initializeFilters() {
    this.search = new TextFilter();
    this.fromDate = new DateFilter();
    this.toDate = new DateFilter();
    this.severityFilters = new MultiSelectFilters(InLineSeveritySelectOptions);
    this.actorFilters = new DynamicMultiSelectFilters([]);
    this.integrationFilters = new MultiSelectFilters([]);

    const inLineAiServiceOptions = Object.values(InLineControlAiService)
      .filter((service) => service !== InLineControlAiService.unknown)
      .map((service) => ({
        id: `${FilterTypesEnum.InLineAiService}-${service}`,
        value: service,
        displayName: getAiServiceDisplayName(service),
        filterType: FilterTypesEnum.InLineAiService,
        categoryName: FilterTypesEnum.InLineAiService.toString(),
        iconPath: getAiServiceLogoPath(service),
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    this.aiServiceFilters = new MultiSelectFilters(inLineAiServiceOptions);
    this.environmentFilters = new DynamicMultiSelectFilters([]);
    this.threadIdFilters = new DynamicMultiSelectFilters([]);
    this._updateAllOptions();
  }

  private _actorOnUpdate(actors: string[]) {
    const actorOptions: ISelectOption[] = actors.map((actor) => ({
      id: `${FilterTypesEnum.InLineActor}-${actor}`,
      value: actor,
      displayName: actor,
      filterType: FilterTypesEnum.InLineActor,
      categoryName: FilterTypesEnum.InLineActor.toString(),
      withoutBeautifyDisplayName: true,
    }));

    const shouldUpdateSelectedOptions = this.actorFilters.updateOptions(actorOptions);
    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _environmentOnUpdate(environmentIds: string[]) {
    const environmentOptions: ISelectOption[] = [];
    environmentIds.forEach((environment) => {
      environmentOptions.push({
        id: environment,
        value: environment,
        displayName: environment,
        filterType: FilterTypesEnum.InLineEnvironment,
        categoryName: FilterTypesEnum.InLineEnvironment,
        withoutBeautifyDisplayName: true,
      } as ISelectOption);
    });

    const shouldUpdateSelectedOptions = this.environmentFilters.updateOptions(Object.values(environmentOptions));

    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _threadIdOnUpdate(threadIds: string[]) {
    const threadOptions: ISelectOption[] = [];
    threadIds.forEach((thread) => {
      threadOptions.push({
        id: thread,
        value: thread,
        displayName: thread,
        filterType: FilterTypesEnum.InLineThreadId,
        categoryName: FilterTypesEnum.InLineThreadId,
        withoutBeautifyDisplayName: true,
      } as ISelectOption);
    });

    const shouldUpdateSelectedOptions = this.threadIdFilters.updateOptions(Object.values(threadOptions));

    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _updateAllOptions() {
    this.allOptions$.next([
      ...this.severityFilters.getOptions$().value,
      ...this.actorFilters.getOptions$().value,
      ...this.integrationFilters.getOptions$().value,
      ...this.aiServiceFilters.getOptions$().value,
      ...this.environmentFilters.getOptions$().value,
      ...this.threadIdFilters.getOptions$().value,
    ]);
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      // Filter integrations based on user's access permissions for step viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.STEP_VIEWER
      );

      const integrationsPlatformSet: Set<string> = new Set();

      const integrationsOptionsMap: ISelectOption[] = filteredIntegrations.map((integration) => {
        integrationsPlatformSet.add(integration.platform);

        const option = fromIntegrationToSelectOption(integration);
        return {
          ...option,
          filterType: FilterTypesEnum.Integration,
          categoryName: FilterTypesEnum.Integration,
        };
      });

      const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  /**
   * Subscribes to inline integrations and builds a mapping from integration ID to inline integration ID
   */
  private _inlineIntegrationsOnUpdate() {
    this._inLineIntegrationsHttpService.getAllInLineIntegrations().subscribe({
      next: (inlineIntegrations: InLineIntegration[]) => {
        // Build the mapping from integration ID to inline integration ID
        this._integrationToInlineIntegrationMap.clear();
        inlineIntegrations.forEach((inlineIntegration) => {
          if (inlineIntegration.integrationId && inlineIntegration.id) {
            this._integrationToInlineIntegrationMap.set(inlineIntegration.integrationId, inlineIntegration.id);
          }
        });
      },
      error: (error) => {
        // If fetching inline integrations fails, log the error but continue without the mapping
        console.warn('Failed to fetch inline integrations for filter mapping:', error);
      },
    });
  }
}
